import { getAnthropicApiKey } from "../lockbox/classification/claudeClassifier.js";

/**
 * @typedef {{ role: 'user'|'assistant', text: string }} HelpChatMessage
 */

/**
 * @param {HelpChatMessage[]} history
 * @param {{
 *   recoverySkipped: boolean,
 *   hasRecoveryEmail: boolean,
 *   lockedPageLabel: string,
 * }} context
 * @returns {Promise<string>}
 */
export async function getHelpChatReply(history, context) {
  const apiKey = getAnthropicApiKey();
  const lastUser = [...history].reverse().find((m) => m.role === "user")?.text || "";

  if (!apiKey) {
    return scriptedHelpReply(lastUser, context);
  }

  const system = `You are Procsystim Screen Time Help — a calm, brief assistant inside a habit-tracking app.
The user forgot their Settings passcode. Guide them without being preachy.

Recovery options in this app (all on-device, no cloud account):
1. If they set a recovery email + recovery password → use "Recover with email" on this screen.
2. If they skipped recovery → use "Verify & reveal" with habit name + best streak from Insights.
3. Never invent a passcode. Only the app can reveal it after verification.

Context: locked area="${context.lockedPageLabel}", recoverySkipped=${context.recoverySkipped}, hasRecoveryEmail=${context.hasRecoveryEmail}.
Keep replies under 80 words.`;

  const messages = history.map((m) => ({
    role: m.role === "user" ? "user" : "assistant",
    content: m.text,
  }));

  try {
    const res = await fetch("https://api.anthropic.com/v1/messages", {
      method: "POST",
      headers: {
        "x-api-key": apiKey,
        "anthropic-version": "2023-06-01",
        "content-type": "application/json",
      },
      body: JSON.stringify({
        model: "claude-3-5-haiku-20241022",
        max_tokens: 300,
        temperature: 0.3,
        system,
        messages,
      }),
    });
    if (!res.ok) throw new Error("api");
    const data = await res.json();
    const text = data?.content?.find((c) => c.type === "text")?.text?.trim();
    return text || scriptedHelpReply(lastUser, context);
  } catch {
    return scriptedHelpReply(lastUser, context);
  }
}

/**
 * @param {string} userText
 * @param {{ recoverySkipped: boolean, hasRecoveryEmail: boolean }} context
 */
function scriptedHelpReply(userText, context) {
  const t = userText.toLowerCase();
  if (t.includes("reveal") || t.includes("passcode") || t.includes("password") || t.includes("forgot")) {
    if (context.hasRecoveryEmail && !context.recoverySkipped) {
      return "Try Recover with email first — enter the recovery email and recovery password you chose when setting the passcode.\n\nIf that fails, scroll to Verify & reveal and answer with your habit name and best streak from Insights.";
    }
    if (context.recoverySkipped) {
      return "You skipped recovery when setting the passcode. Use Verify & reveal below: enter your habit name and best streak exactly as shown on Home / Insights. The app will then show your passcode on this device only.";
    }
    return "Use Recover with email if you set it up, or Verify & reveal with your habit stats. I cannot guess your passcode — the app reveals it only after verification.";
  }
  if (t.includes("recovery") || t.includes("email")) {
    return "Recovery uses the email and recovery password you saved during setup — not a Procsystim cloud login. They are stored only on this phone.";
  }
  return "I'm here to help you unlock Settings safely. Tell me if you set up recovery email, or if you skipped it. I'll point you to the right option on this screen.";
}
